// Imports
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

// Controls
import { Button, Modal, Select, Form } from "@gpstrackit/gtc-react-controls";
import { NotificationContainer } from "Components/Notifications";
import { Alert } from '@mui/material';

// Components
import { TableView } from "@gpstrackit/gtc-react-controls";
import FileUpload from 'Components/FileUpload';

// Hooks
import { useFileUpload , useRealtimeTags} from 'Core/hooks';

// Actions
import { workOrderImportCustomers, reset, workOrderloadFileCsv, workOrderDownloadAttachmentUrl } from "./redux/slice";
import { catalogLoad } from "Redux/actions";

const ImportCustomers = ({ refresh }) => {

    //Language
    const { messages } = useIntl();
    const filesUpload = useFileUpload([]);

    const delimiters = [
        {
            id: ",",
            name: messages['manage_importCustomers_comma']
        },
        {
            id: ";",
            name: messages['manage_importCustomers_semicomma']
        }
    ];

    //Redux States
    const { open, loading, urlFileCsv, csvFile } = useSelector((state) => state.workOrdersImportCustomersRedux);
    const loadingUploadFile = useSelector(state => state.commomRedux.loadingUploadFile);
    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });

    //Redux
    const dispatch = useDispatch();

    //Local States
    const [delimiter, setDelimiter] = useState(delimiters[0]);
    const [fileDataCsv, setFileDataCsv] = useState([]);
    const [errorMessage, setErrorMessage] = useState({
        message: "",
        type: ""
    });

    //Form refs
    const createEditWorkOrderFormRef = Form.useForm();

    //Handlers and utils
    const handleClose = useCallback(() => {
        setDelimiter(delimiters[0])
        setFileDataCsv(setFileDataCsv([]))
        setErrorMessage({ message: "", type: "" })
        dispatch(reset())
    }, []);

    //Function to process CSV
    const csvToArray = (str, delimiter = ",") => {
        str = str.replace('\r', '');
        str = str.replace('(FT)', '');
        const header_cols = str.slice(0, str.indexOf("\n")).split(delimiter);
        const row_data = str.slice(str.indexOf("\n") + 1).split("\n");
        let response = [];
        row_data.forEach((row) => {
            const values = delimiter == ',' ? row.split(/,(?![^"]*"(?:,|$))/) : row.split(delimiter);
            let formatRow = new Object();
            header_cols.forEach((header, index) => {
                if (header && header !== '') {
                    let value = (typeof values[index] !== 'undefined') ? values[index] : '';
                    value = value.replace('\"', '');
                    formatRow[`${header}`] = value.replace('"', '');
                }
            });
            response.push(formatRow);
        });
        return response;
    }

    //Function to validate mandatory fields
    const validateRequiredFields = (data, fields) => {
        for (let field of fields) {
            if (!data?.[field]) {
                return `${field} ${messages['manage_importCustomers_isRequired']}`;
            }
        }
        return '';
    };

    //Function to validate latitude
    const isValidLatitude = (lat) => {
        return /^-?([0-8]?[0-9](\.\d+)?|90(\.0+)?)$/.test(lat);
    }
    //Function to validate longitude
    const isValidLongitude = (lon) => {
        return /^-?((1[0-7][0-9]|[0-9]{1,2})(\.\d+)?|180(\.0+)?)$/.test(lon);
    }

    //Function to validate coordinates
    const validateCoordinates = (latitude, longitude) => {
        if (!latitude || !longitude) return messages['manage_importCustomers_latitudeAndLogitudeRequired'];
        if (!isValidLatitude(latitude)) return messages['manage_importCustomers_latitudeIsInvalid'];
        if (!isValidLongitude(longitude)) return messages['manage_importCustomers_longitudeIsInvalid'];
        return '';
    };
    //Function to validate tags
    const validateTags = useCallback((tagsString) => {
        const validTags = new Set(tags.map(tag => tag.label.toLowerCase()));
        if (!validTags?.size) return ''
        const tagsArray = tagsString.split('/');
        for (let tag of tagsArray) {
            if (!validTags.has(tag.toLowerCase())) {
                return `${messages['manage_importCustomers_tag']} '${tag}' ${messages['manage_importCustomers_doesNotExist']}`;
            }
        }
        return '';
    }, [tags]);

    const UploadInfo = () => {
        if (filesUpload.value.length < 1) {
            setErrorMessage({
                message: messages['manage_importCustomers_requiredFileCSV'],
                type: "error"
            });
            return false;
        }

        dispatch(workOrderDownloadAttachmentUrl({ id: filesUpload.value[0].id }))
    }

    useEffect(() => {
        if (csvFile && open) {
            let ListCustomers = csvToArray(csvFile, delimiter?.id);
            if (ListCustomers?.length && !ListCustomers[0]?.hasOwnProperty('Tags')) {
                setErrorMessage({
                    message: messages['manage_importCustomers_errorFile'],
                    type: "error"
                });

            } else {
                let dataDriverCsv = [];
                let errorCon = false;

                ListCustomers.forEach((data) => {
                    if (data) {
                        let error = '';
                        const requiredFields = ["Name", "LocationName", "Address", "City", "State", "Zip", "Latitude", "Longitude", "ContactName", "Email", "Phone", "CountryCode", "NotifyByPhone", "NotifyByEmail", "Tags"];

                        //Validate if all fields are empty to not process.
                        const allFieldsEmpty = requiredFields.every(field => !data[field]?.trim());
                        if (allFieldsEmpty) return;

                        //Validate required fields
                        error = validateRequiredFields(data, requiredFields);
                        if (!error) {
                            let name = data?.Name?.trim();
                            if (!/^[a-zA-Z0-9\s]+$/.test(name)) {
                                error = messages['manage_importCustomers_nameIsInvalid'];
                            }
                        }
                        if (!error) {
                            //Validate coordinates
                            error = validateCoordinates(data?.Latitude, data?.Longitude);
                        }
                        if (!error) {
                            //Validate the existence of tags
                            error = validateTags(data?.Tags);
                        }

                        dataDriverCsv.push({
                            ...data,
                            error
                        });
                        if (error) errorCon = true;
                    }
                });

                setFileDataCsv(dataDriverCsv);
                if (errorCon) {
                    setErrorMessage({
                        message: messages['manage_importCustomers_pleaseFixErrorsBeforeContinuing'],
                        type: "error"
                    });
                } else {
                    setErrorMessage({
                        message: messages['manage_importCustomers_successMessages'],
                        type: "success"
                    });
                    dispatch(workOrderImportCustomers({ fileKey: filesUpload?.value[0]?.fileKey, delimiter: delimiter?.id }))
                }
            }
            dispatch(reset({ csvFile: null }))
        }
    }, [csvFile, open]);

    useEffect(() => {
        if (urlFileCsv) {
            dispatch(workOrderloadFileCsv({ url: urlFileCsv }))
            dispatch(reset({ urlFileCsv: null }))
        }
    }, [urlFileCsv]);

    useEffect(() => {
        setTimeout(() => {
            createEditWorkOrderFormRef.setValues({
                delimiter: delimiters[0]
            })
        }, 500)
    }, [open])

    useEffect(() => {
        if (open) {
            dispatch(catalogLoad([, "tags"]));
        }
    }, [open])

    useEffect(() => {
        return () => {
            handleClose()
        }
    }, [])

    useEffect(() => {
        if (open && filesUpload?.value?.length && errorMessage?.message === messages['manage_importCustomers_requiredFileCSV']) {
            setErrorMessage({ message: "", type: "" })
        }
    }, [filesUpload.value])

    return (
        <Modal
            title={messages['manage_importCustomers_title']}
            size="small"
            open={open}
            handleClose={handleClose}
            loading={loading}
            footer={
                <div>
                    <Button
                        variant="secondary"
                        style={{ marginRight: "5px" }}
                        onClick={() => {
                            if (fileDataCsv?.length > 0) {
                                setErrorMessage({ message: "", type: "" })
                                setFileDataCsv([])
                                filesUpload.onChange([])
                            } else handleClose()
                        }}
                        isDisabled={loading}
                    >
                        {fileDataCsv?.length > 0 ? messages['back'] : messages["cancel"]}
                    </Button>
                    {
                        fileDataCsv.length === 0 &&
                        <Button
                            variant="primary"
                            onClick={() => {
                                createEditWorkOrderFormRef?.submit()
                            }}
                            isDisabled={loading || loadingUploadFile}
                        >
                            {messages['manage_importCustomers_upload']}
                        </Button>
                    }
                </div >
            }
        >
            <div style={{ padding: 25 }}>
                {errorMessage.message &&
                    <Alert style={{ marginBottom: 10 }} severity={errorMessage.type} onClose={() => { setErrorMessage("") }}>{errorMessage.message}</Alert>
                }
                {
                    fileDataCsv.length === 0 ? (
                        <>
                            <Button
                                isDisabled={loadingUploadFile}
                                style={{ width: '100%', marginBottom: 10 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    let csvContent = `Name${delimiter?.id}EIN${delimiter?.id}LocationName${delimiter?.id}Address${delimiter?.id}City${delimiter?.id}State${delimiter?.id}Zip${delimiter?.id}Latitude${delimiter?.id}Longitude${delimiter?.id}Description${delimiter?.id}ContactName${delimiter?.id}Email${delimiter?.id}Phone${delimiter?.id}CountryCode${delimiter?.id}NotifyByPhone${delimiter?.id}NotifyByEmail${delimiter?.id}Tags\n`;
                                    csvContent += `${delimiter?.id.repeat(16)}"tag1/tag2/tag3"\n`;
                                    let a = document.createElement('a');
                                    a.href = URL.createObjectURL(
                                        new Blob([csvContent], {
                                            type: 'text/csv;encoding:utf-8;charset=UTF-8',
                                        }),
                                    );
                                    a.setAttribute('download', 'customer_format.csv');
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                }}
                            >{messages['manage_importCustomers_downloadTemplate']}</Button>
                            <Form
                                ref={createEditWorkOrderFormRef.ref}
                                loading={loading}
                                onValidSubmit={() => UploadInfo()}
                            >
                                <Form.Item
                                    name="delimiter"
                                    label={messages['manage_importCustomers_delimiter']}
                                    rules={[{ required: true, message: 'required_field' }]}
                                >
                                    <Select
                                        id="delimiter"
                                        options={delimiters}
                                        placeholder={messages['select']}
                                        valueField="id"
                                        textField="name"
                                        style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                        onChange={(value) => {
                                            setDelimiter(value)
                                        }}
                                        isSearchable
                                        isClearable
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="name"
                                    label={messages['manage_importCustomers_fileCSV']}
                                >
                                    <FileUpload
                                        {...filesUpload}
                                        placeholder={messages['manage_importCustomers_chooseFile']}
                                        types=".csv"
                                        helpText={messages['manage_importCustomers_fileHelpText']}
                                        maxSize={5}
                                        disabled={filesUpload?.value?.length > 0}
                                        maxSizeMessage={`${messages['max']}: 5MB`}
                                    />
                                    <small className="form-text text-muted">{messages['manage_importCustomers_maxSizeFile']}</small>
                                </Form.Item>
                            </Form>
                        </>

                    ) : (
                        <TableView
                            data={fileDataCsv || []}
                            loading={loading}
                            hidePagination
                            disableItemSelection
                            topheight={240}
                            columns={[
                                {
                                    key: "name",
                                    title: messages['manage_importCustomers_name'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Name}</span>
                                },
                                {
                                    key: "ein",
                                    title: messages['manage_importCustomers_ein'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.EIN}</span>
                                },
                                {
                                    key: "locationName",
                                    title: messages['manage_importCustomers_locationName'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.LocationName}</span>
                                },
                                {
                                    key: "city",
                                    title: messages['manage_importCustomers_city'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.City}</span>
                                },
                                {
                                    key: "state",
                                    title: messages['manage_importCustomers_state'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.State}</span>
                                },
                                {
                                    key: "zip",
                                    title: messages['manage_importCustomers_zip'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Zip}</span>
                                },
                                {
                                    key: "latitude",
                                    title: messages['manage_importCustomers_latitude'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Latitude}</span>
                                },
                                {
                                    key: "longitude",
                                    title: messages['manage_importCustomers_longitude'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Longitude}</span>
                                },
                                {
                                    key: "description",
                                    title: messages['manage_importCustomers_description'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Description}</span>
                                },
                                {
                                    key: "contactName",
                                    title: messages['manage_importCustomers_contactName'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.ContactName}</span>
                                },
                                {
                                    key: "email",
                                    title: messages['manage_importCustomers_email'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Email}</span>
                                },
                                {
                                    key: "phone",
                                    title: messages['manage_importCustomers_phone'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Phone}</span>
                                },
                                {
                                    key: "countryCode",
                                    title: messages['manage_importCustomers_countryCode'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.CountryCode}</span>
                                },
                                {
                                    key: "notifyByPhone",
                                    title: messages['manage_importCustomers_notifyByPhone'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.NotifyByPhone}</span>
                                },
                                {
                                    key: "notifyByEmail",
                                    title: messages['manage_importCustomers_notifyByEmail'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.NotifyByEmail}</span>
                                },
                                {
                                    key: "tags",
                                    title: messages['manage_importCustomers_tags'],
                                    show: true,
                                    width: 150,
                                    render: (row) => <span>{row?.Tags}</span>
                                },
                                {
                                    key: "error",
                                    title: messages['manage_importCustomers_error'],
                                    show: true,
                                    width: 200,
                                    render: (row) => <span>{row?.error}</span>
                                },
                            ]}
                        />
                    )
                }
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default ImportCustomers;